import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChartDataPoint, LineConfig } from "./useD3LineChart";
import {
  discoverFactorsService,
  type DiscoverFactorProductType,
  type DiscoverFactorsBatchRequest,
} from "../services/discoverFactorsService";
import type { FactorEntry, FactorsChartEntityType } from "../services/productFactorsService";
import {
  getBucketMsForFilter,
  getV3FilterWindowBounds,
  V3_FACTOR_SLOT_MS,
  V3_INVESTOR_TIME_FILTER_OPTIONS,
  V3_TIME_FILTER_OPTIONS,
  type V3TimeFilter,
} from "../components/performanceChartV3Utils";

export const STRATEGY_COMPARISON_ALL_KEY = "all";
export const STRATEGY_COMPARISON_STRATEGY_KEY = "strategy";
export const STRATEGY_COMPARISON_STRATEGY_STROKE = "white";

const PRODUCT_FACTORS_PAGE_SIZE = 10000;
const DEFAULT_CHART_HEIGHT = 560;
const DEFAULT_SELECTED_PERIOD: V3TimeFilter = "Since inception";
const DEFAULT_STRATEGY_FILTER_OPTIONS = V3_TIME_FILTER_OPTIONS.filter(
  (option) => option.value !== "All"
);
const DEFAULT_STRATEGY_FILTER_VALUES = DEFAULT_STRATEGY_FILTER_OPTIONS.map(
  (option) => option.value
) as V3TimeFilter[];

export interface StrategyComparisonProduct {
  key: string;
  entityType: Extract<FactorsChartEntityType, "PORTFOLIO" | "BASKET">;
  productType: DiscoverFactorProductType;
  productId: number;
  providerId: number;
  label: string;
  addedAtUtc: string | null;
  entityCreatedAt?: string | null;
  color: string;
}

export interface UseStrategyComparisonChartDataProps {
  isActive: boolean;
  selectedKey: string;
  strategyFactors: FactorEntry[];
  products: StrategyComparisonProduct[];
  showTodayFilter?: boolean;
}

export interface StrategyComparisonChartDataResult {
  loading: boolean;
  error: string | null;
  chartData: ChartDataPoint[];
  lines: LineConfig[];
  chartHeight: number;
  timeFilterOptions: ReadonlyArray<{ readonly value: V3TimeFilter; readonly label: string }>;
  availableFilters: V3TimeFilter[];
  selectedPeriod: V3TimeFilter;
  handleSelectPeriod: (value: V3TimeFilter) => void;
  retry?: () => void;
}

interface SeriesInput {
  id: string;
  label: string;
  color: string;
  factors: FactorEntry[];
  startAtUtc: string | null;
  /** Actual performance is a UTC date-only series whose Today row is inclusive. */
  todayWindow?: "local" | "utc-date-inclusive";
}

function parseTimeMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function parseFactorValue(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function sortFactorsAscending(factors: FactorEntry[]): FactorEntry[] {
  return factors
    .map((factor) => {
      const value = parseFactorValue(factor.f);
      return value === null ? null : { t: factor.t, f: value };
    })
    .filter((factor): factor is FactorEntry => factor !== null && parseTimeMs(factor.t) !== null)
    .sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
}

function getProductCacheKey(product: StrategyComparisonProduct): string {
  return `${product.key}:${product.providerId}:${product.addedAtUtc ?? ""}`;
}

function getProductLineId(product: StrategyComparisonProduct): string {
  return `product_${product.entityType.toLowerCase()}_${product.productId}`;
}

function getVisibleProducts(
  selectedKey: string,
  products: StrategyComparisonProduct[]
): StrategyComparisonProduct[] {
  if (selectedKey === STRATEGY_COMPARISON_ALL_KEY) return products;
  const selected = products.find((product) => product.key === selectedKey);
  return selected ? [selected] : [];
}

function getProductsSignature(products: StrategyComparisonProduct[]): string {
  return JSON.stringify(products.map((product) => ({
    key: product.key,
    entityType: product.entityType,
    productType: product.productType,
    productId: product.productId,
    providerId: product.providerId,
    label: product.label,
    addedAtUtc: product.addedAtUtc,
    entityCreatedAt: product.entityCreatedAt ?? null,
    color: product.color,
  })));
}

function getSeriesAnchorMs(series: SeriesInput[]): number | null {
  const latestTimes = series
    .map((item) => {
      const sorted = sortFactorsAscending(item.factors);
      return sorted.length > 0 ? parseTimeMs(sorted[sorted.length - 1].t) : null;
    })
    .filter((value): value is number => value !== null);

  if (latestTimes.length === 0) return null;
  return Math.max(...latestTimes);
}

function getSeriesBaselineMs(
  series: SeriesInput,
  period: V3TimeFilter,
  anchor: Date
): number | null {
  const sorted = sortFactorsAscending(series.factors);
  if (sorted.length === 0) return null;

  const firstFactorMs = parseTimeMs(sorted[0].t);
  if (firstFactorMs === null) return null;

  const configuredStartMs = parseTimeMs(series.startAtUtc);
  const inceptionStartMs = configuredStartMs ?? firstFactorMs - V3_FACTOR_SLOT_MS;
  if (period === "Today" && series.todayWindow === "utc-date-inclusive") {
    const now = new Date();
    const utcTodayMs = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    );
    return Math.max(inceptionStartMs, utcTodayMs - V3_FACTOR_SLOT_MS);
  }
  const { start } = getV3FilterWindowBounds(period, anchor, null);
  const periodStartMs = start ? start.getTime() : null;

  if (periodStartMs === null) return inceptionStartMs;
  return Math.max(inceptionStartMs, periodStartMs);
}

function buildSeriesPoints(
  series: SeriesInput,
  period: V3TimeFilter,
  anchorMs: number
): ChartDataPoint[] {
  const sorted = sortFactorsAscending(series.factors);
  if (sorted.length === 0) return [];

  const anchor = new Date(anchorMs);
  const baselineMs = getSeriesBaselineMs(series, period, anchor);
  if (baselineMs === null || baselineMs > anchorMs) return [];

  const bucketMs = getBucketMsForFilter(period);
  const buckets = new Map<number, number[]>();

  sorted.forEach((factor) => {
    const factorMs = parseTimeMs(factor.t);
    // Exclusive window start: the factor on the baseline date is the 0%
    // reference point, so its return belongs to the prior period. This matches
    // the metric functions (strategyPerfMetrics, computeActualPeriodReturn),
    // which all use a strict `>` start, so a line's endpoint equals its metric.
    if (factorMs === null || factorMs <= baselineMs || factorMs > anchorMs) return;
    const bucketIndex = Math.max(1, Math.ceil((factorMs - baselineMs) / bucketMs));
    const bucketEndMs = baselineMs + bucketIndex * bucketMs;
    const existing = buckets.get(bucketEndMs);
    if (existing) {
      existing.push(factor.f);
    } else {
      buckets.set(bucketEndMs, [factor.f]);
    }
  });

  const points: ChartDataPoint[] = [
    {
      date: new Date(baselineMs).toISOString(),
      fullDate: new Date(baselineMs).toISOString(),
      [series.id]: 0,
    },
  ];

  let cumulative = 1;
  [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .forEach(([bucketMsValue, factors]) => {
      factors.forEach((factor) => {
        cumulative *= factor;
      });
      const timestamp = new Date(Math.min(bucketMsValue, anchorMs)).toISOString();
      points.push({
        date: timestamp,
        fullDate: timestamp,
        [series.id]: (cumulative - 1) * 100,
      });
    });

  return points;
}

function buildMergedChartData(
  series: SeriesInput[],
  period: V3TimeFilter
): ChartDataPoint[] {
  const anchorMs = getSeriesAnchorMs(series);
  if (anchorMs === null) return [];

  const byDate = new Map<string, ChartDataPoint>();
  series.forEach((item) => {
    buildSeriesPoints(item, period, anchorMs).forEach((point) => {
      const fullDate = point.fullDate ?? point.date;
      const existing = byDate.get(fullDate) ?? { date: fullDate, fullDate };
      existing[item.id] = point[item.id];
      byDate.set(fullDate, existing);
    });
  });

  return [...byDate.values()].sort(
    (a, b) => new Date(a.fullDate ?? a.date).getTime() - new Date(b.fullDate ?? b.date).getTime()
  );
}

function seriesHasFactorForPeriod(
  series: SeriesInput,
  period: V3TimeFilter,
  anchorMs: number
): boolean {
  const anchor = new Date(anchorMs);
  const baselineMs = getSeriesBaselineMs(series, period, anchor);
  if (baselineMs === null || baselineMs > anchorMs) return false;

  return sortFactorsAscending(series.factors).some((factor) => {
    const factorMs = parseTimeMs(factor.t);
    return factorMs !== null && factorMs > baselineMs && factorMs <= anchorMs;
  });
}

function getAvailableFilters(
  series: SeriesInput[],
  filterValues: V3TimeFilter[] = DEFAULT_STRATEGY_FILTER_VALUES
): V3TimeFilter[] {
  const anchorMs = getSeriesAnchorMs(series);
  if (anchorMs === null) return ["Since inception"];

  return filterValues.filter((period) => {
    if (period === "Today") {
      return series.some((item) => seriesHasFactorForPeriod(item, period, anchorMs));
    }

    const chartData = buildMergedChartData(series, period);
    return chartData.some((point) =>
      series.some((item) => typeof point[item.id] === "number")
    );
  });
}

function getDefaultSelectedPeriod(availableFilters: V3TimeFilter[]): V3TimeFilter {
  return availableFilters.includes(DEFAULT_SELECTED_PERIOD)
    ? DEFAULT_SELECTED_PERIOD
    : "Since inception";
}

export function useStrategyComparisonChartData({
  isActive,
  selectedKey,
  strategyFactors,
  products,
  showTodayFilter = false,
}: UseStrategyComparisonChartDataProps): StrategyComparisonChartDataResult {
  const [productFactorsByCacheKey, setProductFactorsByCacheKey] = useState(
    () => new Map<string, FactorEntry[] | null>()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<V3TimeFilter>(DEFAULT_SELECTED_PERIOD);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const fetchRunIdRef = useRef(0);
  const userSelectedPeriodRef = useRef(false);

  // Some callers assemble an equivalent products array during render. Keep a
  // content-stable reference so loading/error state updates cannot restart the
  // same request or create an unbounded retry loop.
  const productsSignature = getProductsSignature(products);
  const stableProductsRef = useRef({
    signature: productsSignature,
    value: products,
  });
  if (stableProductsRef.current.signature !== productsSignature) {
    stableProductsRef.current = { signature: productsSignature, value: products };
  }
  const stableProducts = stableProductsRef.current.value;

  const visibleProducts = useMemo(
    () => getVisibleProducts(selectedKey, stableProducts),
    [selectedKey, stableProducts]
  );
  const timeFilterOptions = useMemo(
    () =>
      (showTodayFilter ? V3_INVESTOR_TIME_FILTER_OPTIONS : V3_TIME_FILTER_OPTIONS).filter(
        (option) => option.value !== "All"
      ),
    [showTodayFilter]
  );
  const filterValues = useMemo(
    () => timeFilterOptions.map((option) => option.value) as V3TimeFilter[],
    [timeFilterOptions]
  );

  const prevIsActiveRef = useRef(isActive);
  useEffect(() => {
    const wasInactive = !prevIsActiveRef.current;
    prevIsActiveRef.current = isActive;
    if (isActive && wasInactive) {
      userSelectedPeriodRef.current = false;
      setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      fetchRunIdRef.current += 1;
      setLoading(false);
      return;
    }
    const productsToFetch = visibleProducts.filter(
      (product) => product.addedAtUtc && !productFactorsByCacheKey.has(getProductCacheKey(product))
    );

    if (productsToFetch.length === 0) {
      fetchRunIdRef.current += 1;
      setLoading(false);
      const hasFailedVisibleProduct = visibleProducts.some(
        (product) => productFactorsByCacheKey.get(getProductCacheKey(product)) === null
      );
      setError(
        hasFailedVisibleProduct
          ? "Failed to load some product comparison data."
          : null
      );
      return;
    }

    const runId = ++fetchRunIdRef.current;
    setLoading(true);
    setError(null);

    const requests: DiscoverFactorsBatchRequest[] = productsToFetch.map((product) => ({
      providerId: product.providerId,
      productType: product.productType,
      productId: product.productId,
      from: product.addedAtUtc ?? undefined,
      pageSize: PRODUCT_FACTORS_PAGE_SIZE,
    }));

    discoverFactorsService
      .getFactorsBatch(requests)
      .then((results) => {
        if (runId !== fetchRunIdRef.current) return;
        const hadFailures = results.some((result) => result.failed);
        setProductFactorsByCacheKey((previous) => {
          const next = new Map(previous);
          results.forEach((result) => {
            const product = productsToFetch.find(
              (item) =>
                item.providerId === result.providerId &&
                item.productType === result.productType &&
                item.productId === result.productId
            );
            if (!product) return;
            next.set(
              getProductCacheKey(product),
              result.failed ? null : sortFactorsAscending(result.factors)
            );
          });
          return next;
        });
        if (hadFailures) {
          setError("Failed to load some product comparison data.");
        }
      })
      .catch((err) => {
        if (runId !== fetchRunIdRef.current) return;
        console.error("Failed to load strategy product comparison factors:", err);
        setError("Failed to load product comparison data.");
      })
      .finally(() => {
        if (runId === fetchRunIdRef.current) {
          setLoading(false);
        }
      });
  }, [isActive, productFactorsByCacheKey, retryAttempt, visibleProducts]);

  const series = useMemo<SeriesInput[]>(() => {
    const output: SeriesInput[] = [];

    if (
      (selectedKey === STRATEGY_COMPARISON_ALL_KEY
        || selectedKey === STRATEGY_COMPARISON_STRATEGY_KEY)
      && strategyFactors.length > 0
    ) {
      output.push({
        id: STRATEGY_COMPARISON_STRATEGY_KEY,
        label: "Strategy",
        color: STRATEGY_COMPARISON_STRATEGY_STROKE,
        factors: strategyFactors,
        startAtUtc: null,
      });
    }

    visibleProducts.forEach((product) => {
      const factors = productFactorsByCacheKey.get(getProductCacheKey(product)) ?? [];
      if (factors.length === 0) return;
      output.push({
        id: getProductLineId(product),
        label: product.label,
        color: product.color,
        factors,
        startAtUtc: product.addedAtUtc,
      });
    });

    return output;
  }, [productFactorsByCacheKey, selectedKey, strategyFactors, visibleProducts]);

  const availableFilters = useMemo(
    () => getAvailableFilters(series, filterValues),
    [filterValues, series]
  );

  useEffect(() => {
    userSelectedPeriodRef.current = false;
    setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
  }, [selectedKey]);

  useEffect(() => {
    if (
      !userSelectedPeriodRef.current &&
      selectedPeriod !== DEFAULT_SELECTED_PERIOD &&
      availableFilters.includes(DEFAULT_SELECTED_PERIOD)
    ) {
      setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
      return;
    }

    if (!availableFilters.includes(selectedPeriod)) {
      setSelectedPeriod(getDefaultSelectedPeriod(availableFilters));
    }
  }, [availableFilters, selectedPeriod]);

  const handleSelectPeriod = useCallback((value: V3TimeFilter) => {
    userSelectedPeriodRef.current = true;
    setSelectedPeriod(value);
  }, []);

  const retry = useCallback(() => {
    setProductFactorsByCacheKey((previous) => {
      const next = new Map(previous);
      for (const [key, factors] of next) {
        if (factors === null) next.delete(key);
      }
      return next;
    });
    setRetryAttempt((current) => current + 1);
  }, []);

  const chartData = useMemo(
    () => buildMergedChartData(series, selectedPeriod),
    [selectedPeriod, series]
  );

  const lines = useMemo<LineConfig[]>(
    () =>
      series.map((item) => ({
        dataKey: item.id,
        stroke: item.color,
        strokeWidth: item.id === STRATEGY_COMPARISON_STRATEGY_KEY ? 2.5 : 1.8,
        label: item.label,
        showDots: false,
      })),
    [series]
  );

  return {
    loading,
    error,
    chartData,
    lines,
    chartHeight: DEFAULT_CHART_HEIGHT,
    timeFilterOptions,
    availableFilters,
    selectedPeriod,
    handleSelectPeriod,
    retry,
  };
}

export type { SeriesInput };

export {
  buildMergedChartData,
  getAvailableFilters,
  getSeriesAnchorMs,
  seriesHasFactorForPeriod,
};

export const strategyComparisonChartTestUtils = {
  buildMergedChartData,
  getAvailableFilters,
  getProductLineId,
};
